import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Phone, MessageSquare, CheckCircle, Settings, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { businessInfo } from '../data/mock';

const MaintenancePage = () => {
  const maintenanceItems = [
    "Spring tension adjustment and lubrication",
    "Roller inspection and replacement if worn",
    "Cable inspection for fraying or damage",
    "Track cleaning and alignment check",
    "Hardware tightening (bolts, brackets, hinges)",
    "Safety sensor testing and alignment",
    "Opener force adjustment",
    "Weather seal inspection",
    "Door balance testing",
    "Complete safety system test"
  ];

  const benefits = [
    "Extend door lifespan by years",
    "Prevent costly emergency repairs",
    "Maintain warranty coverage",
    "Ensure family safety",
    "Quieter, smoother operation",
    "Lower energy costs (better seal)",
    "Early problem detection",
    "Peace of mind"
  ];

  const warrantyInfo = [
    "Service includes inspection report",
    "Parts warranty on replaced components",
    "Labor warranty on adjustments",
    "Galvanized steel parts used",
    "High-cycle spring lubrication",
    "Safety system certification"
  ];

  const faqItems = [
    {
      q: "How often should Saskatoon homeowners service their garage door?",
      a: "We recommend annual professional maintenance for Saskatoon homes. Saskatoon's temperature extremes stress garage door components. Annual service includes 20-point inspection, lubrication with cold-weather lubricant, spring adjustment, safety testing, and preventive care. Regular maintenance prevents 90% of garage door problems."
    },
    {
      q: "What's included in your maintenance service?",
      a: "Our comprehensive 20-point inspection covers springs, cables, rollers, tracks, hardware, safety sensors, opener, and weather seals. We lubricate moving parts with professional cold-weather lubricant, adjust spring tension, test safety systems, tighten all hardware, and provide detailed service report. All parts are galvanized steel for Saskatoon durability."
    },
    {
      q: "Does maintenance include warranty coverage?",
      a: "Yes! Our maintenance service includes labor warranty on all adjustments and repairs performed during service. Any parts replaced carry manufacturer warranty. Regular maintenance also maintains your door's original warranty - most manufacturers require annual professional service to keep warranty valid for Saskatoon homeowners."
    },
    {
      q: "Can maintenance prevent expensive repairs?",
      a: "Absolutely! Regular maintenance catches small issues before they become major problems. A fraying cable caught during inspection costs much less than emergency cable replacement after it breaks. Spring adjustment during maintenance prevents premature failure. Saskatoon homeowners save hundreds with preventive maintenance versus emergency repairs."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="pt-32 pb-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">Garage Door Maintenance Saskatoon</h1>
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              Professional preventive maintenance for residential and commercial garage doors in Saskatoon. Annual service keeps your door running smoothly and prevents costly emergency repairs. Includes warranty coverage.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button onClick={() => window.location.href = `tel:${businessInfo.phone}`} size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-10 py-6 text-lg">
                <Phone size={22} className="mr-2" />Call {businessInfo.phone}
              </Button>
              <Button onClick={() => window.location.href = '/quote'} size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-10 py-6 text-lg">
                <MessageSquare size={22} className="mr-2" />Schedule Service
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto prose prose-lg max-w-none text-gray-700 space-y-6">
            <h2 className="text-3xl font-bold text-gray-900">Why Maintenance Matters in Saskatoon</h2>
            <p>
              Saskatoon's extreme temperature swings - from -40°C winters to +35°C summers - are hard on garage doors. Metal expands and contracts. Lubricants thicken. Springs lose tension. Regular professional maintenance keeps your door operating safely and reliably through all seasons.
            </p>
            <p>
              At 306 Garage Doors, we provide comprehensive 20-point maintenance service for Saskatoon residential and commercial properties. Our service includes inspection, lubrication, adjustment, testing, and preventive care using professional-grade materials designed for our climate.
            </p>
            
            <h3 className="text-2xl font-bold text-gray-900 mt-8">Residential Maintenance Service</h3>
            <p>
              For Saskatoon homeowners, annual maintenance is an investment that pays off in reliability, safety, and longevity. Our residential service includes:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Complete inspection of all components</li>
              <li>Lubrication with cold-weather lubricant</li>
              <li>Spring tension adjustment and testing</li>
              <li>Safety sensor testing and alignment</li>
              <li>Hardware tightening (galvanized components)</li>
              <li>Weather seal inspection and replacement</li>
              <li>Detailed service report with photos</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mt-8">Commercial Maintenance Programs</h3>
            <p>
              Commercial properties benefit from scheduled maintenance programs. For Saskatoon businesses, we offer:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Quarterly or annual maintenance schedules</li>
              <li>Priority emergency service</li>
              <li>Comprehensive inspection and service</li>
              <li>High-cycle spring maintenance</li>
              <li>Commercial-grade galvanized parts</li>
              <li>Detailed maintenance records</li>
              <li>Warranty coverage on service</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">20-Point Maintenance Inspection</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {maintenanceItems.map((item, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-1" />
                      <p className="text-gray-800 font-semibold">{item}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Benefits of Regular Maintenance</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, idx) => (
                <Card key={idx} className="border-2 border-blue-200">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle size={24} className="text-blue-600 flex-shrink-0 mt-1" />
                      <p className="text-gray-800 font-bold text-lg">{benefit}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Maintenance Service Warranty</h2>
            <Card className="border-2 border-blue-600">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <Shield size={48} className="text-blue-600 flex-shrink-0" />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Warranty Coverage on Service</h3>
                    <p className="text-gray-700 text-lg mb-4">
                      Our maintenance service includes warranty protection on labor, adjustments, and any parts replaced during service.
                    </p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {warrantyInfo.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-1" />
                      <p className="text-gray-800 font-semibold">{item}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqItems.map((item, idx) => (
                <Card key={idx} className="border-2">
                  <CardHeader>
                    <CardTitle className="text-xl text-gray-900">{item.q}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 leading-relaxed">{item.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-600">
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Schedule Maintenance in Saskatoon</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Protect your investment with annual professional maintenance. Contact 306 Garage Doors for comprehensive service with warranty coverage.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button onClick={() => window.location.href = `tel:${businessInfo.phone}`} size="lg" className="bg-white text-blue-600 hover:bg-gray-100 font-bold px-12 py-8 text-xl">
              <Phone size={24} className="mr-2" />Call {businessInfo.phone}
            </Button>
            <Button onClick={() => window.location.href = '/quote'} size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-12 py-8 text-xl">
              Schedule Service
            </Button>
          </div>
          <p className="mt-6 text-blue-100 italic">{businessInfo.confidentialMessage}</p>
        </div>
      </section>
    </div>
  );
};

export default MaintenancePage;